import Snackbar from '@/components/core/Snackbar';
import { CenteredSpinner } from '@/se/components/Spinner';
import { ApolloProvider, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { format } from 'date-fns';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { OrganizationIdProvider } from '@/contexts/OrganizationIdContext';

import { createApolloClient } from '@/essentials/apolloClient';
import { UnauthorizedIndicator } from '../../core/UnauthorizedIndicator';

import connectionProviderContext from './connectionProviderContext';
import type { OnUnauthorizedCallback } from '@/essentials/apolloClient.types';
import type { SubscriptionClient } from 'subscriptions-transport-ws';

interface Props {
  accessToken: string;
  organizationId?: string | number;
  onUnauthorized?: OnUnauthorizedCallback;
  children?: React.ReactNode;
}

function ConnectionProvider(props: Props) {
  const { accessToken, organizationId, onUnauthorized, children } = props;

  const [lastDisconnectedAt, setLastDisconnectedAt] = useState<Date | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null);

  const apolloClientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef<boolean>(false);
  const lastDisconnectedAtRef = useRef<Date | null>(lastDisconnectedAt);

  useEffect(() => {
    lastDisconnectedAtRef.current = lastDisconnectedAt;
  }, [lastDisconnectedAt]);

  const cleanupApollo = useCallback(() => {
    if (apolloClientRef.current) {
      apolloClientRef.current.stop();
      apolloClientRef.current = null;
    }
    if (subscriptionClientRef.current) {
      subscriptionClientRef.current.close();
      subscriptionClientRef.current = null;
    }
  }, []);

  const setLastDisconnected = useCallback((value: Date | null) => {
    if (mountedRef.current) {
      setLastDisconnectedAt(value);
    }
    timerRef.current = null;
  }, []);

  const scheduleSwitch = useCallback(
    (after: number, value: Date | null) => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        setLastDisconnected(value);
      }, after) as unknown as number;
    },
    [setLastDisconnected]
  );

  const onConnected = useCallback(() => {
    scheduleSwitch(100, null);
    setConnected(true);
  }, [scheduleSwitch]);

  const onDisconnected = useCallback(() => {
    if (lastDisconnectedAtRef.current == null) {
      setConnected(false);
      scheduleSwitch(10000, new Date());
    }
  }, [scheduleSwitch]);

  const onUnauthorizedDefault = useCallback<OnUnauthorizedCallback>((_error: Error) => {
    if (mountedRef.current) {
      setConnected(false);
      setUnauthorized(true);
    }
  }, []);

  const onUnrecoverableError = useCallback((error: Error | string) => {
    console.error('[ConnectionProvider] Unrecoverable error - preventing reconnection:', error);
    if (mountedRef.current) {
      setConnected(false);
      setUnauthorized(true);
      setLastDisconnectedAt(null);

      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    const orgIdNumber = organizationId != null ? parseInt(String(organizationId), 10) : Number.NaN;

    const effectiveOnUnauthorized = onUnauthorized || onUnauthorizedDefault;

    const [apolloClient, subscriptionClient] = createApolloClient(
      accessToken,
      orgIdNumber,
      effectiveOnUnauthorized,
      onConnected,
      onDisconnected,
      onUnrecoverableError
    );

    apolloClientRef.current = apolloClient;
    subscriptionClientRef.current = subscriptionClient;
    setClient(apolloClient);

    return () => {
      cleanupApollo();
    };
  }, [accessToken, organizationId, onConnected, onDisconnected, onUnrecoverableError, cleanupApollo]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  if (!client) {
    return <CenteredSpinner size={'5rem'} />;
  }

  const organizationIdStr = organizationId !== undefined ? String(organizationId) : undefined;

  return (
    <OrganizationIdProvider value={organizationIdStr}>
      <ApolloProvider client={client}>
        <connectionProviderContext.Provider
          value={{
            accessToken,
            organizationId: organizationIdStr,
            connected,
          }}
        >
          {lastDisconnectedAt && (
            <Snackbar severity="warning">
              The system is currently offline. Attempting to restore connection...&nbsp;
              <Fragment>
                <span>{`Last time info received ${format(lastDisconnectedAt, 'MM/DD/YYYY')} @`}&nbsp;</span>
                <strong>{format(lastDisconnectedAt, 'HH:mm')}</strong>
              </Fragment>
            </Snackbar>
          )}
          {children}
          {unauthorized && <UnauthorizedIndicator />}
        </connectionProviderContext.Provider>
      </ApolloProvider>
    </OrganizationIdProvider>
  );
}

export default ConnectionProvider;
